import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import IncludeSideBar from "../../Components/Sidebar/IncludeSideBar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AnalyticsChart from "./AnalyticsChart";
import { MEDIA_SERVER_URL } from "../../constants/constants";
import "./UserAnalyticsDetail.css";

const UserAnalyticsDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const analyticsApis = require("../../apis/analytics");

  const [startDate, setStartDate] = useState(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)); // 1 year ago
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  // Analytics data
  const [videoViews, setVideoViews] = useState(null);
  const [feedViews, setFeedViews] = useState(null);
  const [coinAnalytics, setCoinAnalytics] = useState(null);
  const [sentCoins, setSentCoins] = useState(null);
  const [commentAnalytics, setCommentAnalytics] = useState(null);
  const [likeAnalytics, setLikeAnalytics] = useState(null);
  const [followAnalytics, setFollowAnalytics] = useState(null);
  const [followAnalyticsByUser, setFollowAnalyticsByUser] = useState(null);
  const [userInteractions, setUserInteractions] = useState(null);

  // Accordion state for country breakdown
  const [expandedCountries, setExpandedCountries] = useState(new Set());

  // Format date for API
  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Format time in decimal hours to readable string (e.g., "4h 50m" or "45m")
  const formatTime = (hoursDecimal) => {
    if (!hoursDecimal || hoursDecimal === 0) return "0m";
    const totalMinutes = Math.round(hoursDecimal * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Parse country name (handle JSON format like ["Pakistan"])
  const parseCountryName = (country) => {
    if (!country || country === "null" || country === "undefined") return null;

    // Try to parse as JSON if it looks like an array
    if (typeof country === 'string' && country.startsWith('[')) {
      try {
        const parsed = JSON.parse(country);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed[0];
        }
      } catch (e) {
        // If parsing fails, treat as regular string
      }
    }
    return country;
  };

  // Country code to full name mapping
  const countryCodeMap = {
    'PK': 'Pakistan',
    'DE': 'Germany',
    'TR': 'Turkey',
    'MA': 'Morocco',
    'UK': 'United Kingdom',
    'GB': 'United Kingdom',
    'CA': 'Canada',
    'US': 'United States',
    'FR': 'France',
    'ES': 'Spain',
    'IT': 'Italy',
    'NL': 'Netherlands',
    'BE': 'Belgium',
    'AT': 'Austria',
    'CH': 'Switzerland',
    'PL': 'Poland',
    'CZ': 'Czech Republic',
    'HU': 'Hungary',
    'RO': 'Romania',
    'BG': 'Bulgaria',
    'HR': 'Croatia',
    'SI': 'Slovenia',
    'SK': 'Slovakia',
    'LT': 'Lithuania',
    'LV': 'Latvia',
    'EE': 'Estonia',
    'FI': 'Finland',
    'SE': 'Sweden',
    'NO': 'Norway',
    'DK': 'Denmark',
    'IS': 'Iceland',
    'IE': 'Ireland',
    'PT': 'Portugal',
    'GR': 'Greece',
    'CY': 'Cyprus',
    'MT': 'Malta',
    'LU': 'Luxembourg',
    'MC': 'Monaco',
    'LI': 'Liechtenstein',
    'SM': 'San Marino',
    'AD': 'Andorra',
    'VA': 'Vatican City',
    'ME': 'Montenegro',
    'MK': 'North Macedonia',
    'AL': 'Albania',
    'BA': 'Bosnia and Herzegovina',
    'RS': 'Serbia',
    'XK': 'Kosovo',
    'MD': 'Moldova',
    'UA': 'Ukraine',
    'BY': 'Belarus',
    'RU': 'Russia',
    'KZ': 'Kazakhstan',
    'UZ': 'Uzbekistan',
    'KG': 'Kyrgyzstan',
    'TJ': 'Tajikistan',
    'TM': 'Turkmenistan',
    'AZ': 'Azerbaijan',
    'AM': 'Armenia',
    'GE': 'Georgia',
    'TR': 'Turkey',
    'IR': 'Iran',
    'IQ': 'Iraq',
    'SY': 'Syria',
    'LB': 'Lebanon',
    'JO': 'Jordan',
    'IL': 'Israel',
    'PS': 'Palestine',
    'SA': 'Saudi Arabia',
    'YE': 'Yemen',
    'OM': 'Oman',
    'AE': 'United Arab Emirates',
    'QA': 'Qatar',
    'BH': 'Bahrain',
    'KW': 'Kuwait',
    'EG': 'Egypt',
    'LY': 'Libya',
    'TN': 'Tunisia',
    'DZ': 'Algeria',
    'MR': 'Mauritania',
    'SD': 'Sudan',
    'SS': 'South Sudan',
    'ET': 'Ethiopia',
    'ER': 'Eritrea',
    'DJ': 'Djibouti',
    'SO': 'Somalia',
    'KE': 'Kenya',
    'UG': 'Uganda',
    'TZ': 'Tanzania',
    'RW': 'Rwanda',
    'BI': 'Burundi',
    'CD': 'DR Congo',
    'CG': 'Congo',
    'GA': 'Gabon',
    'GQ': 'Equatorial Guinea',
    'CM': 'Cameroon',
    'CF': 'Central African Republic',
    'TD': 'Chad',
    'NG': 'Nigeria',
    'NE': 'Niger',
    'BF': 'Burkina Faso',
    'ML': 'Mali',
    'GN': 'Guinea',
    'SN': 'Senegal',
    'GM': 'Gambia',
    'GW': 'Guinea-Bissau',
    'SL': 'Sierra Leone',
    'LR': 'Liberia',
    'CI': 'Ivory Coast',
    'GH': 'Ghana',
    'TG': 'Togo',
    'BJ': 'Benin',
    'ZA': 'South Africa',
    'NA': 'Namibia',
    'BW': 'Botswana',
    'ZW': 'Zimbabwe',
    'MZ': 'Mozambique',
    'ZM': 'Zambia',
    'MW': 'Malawi',
    'AO': 'Angola',
    'LS': 'Lesotho',
    'SZ': 'Eswatini',
    'MG': 'Madagascar',
    'MU': 'Mauritius',
    'SC': 'Seychelles',
    'KM': 'Comoros',
    'CV': 'Cape Verde',
    'ST': 'Sao Tome and Principe',
    'IN': 'India',
    'CN': 'China',
    'JP': 'Japan',
    'KR': 'South Korea',
    'KP': 'North Korea',
    'TW': 'Taiwan',
    'HK': 'Hong Kong',
    'MO': 'Macau',
    'MN': 'Mongolia',
    'VN': 'Vietnam',
    'LA': 'Laos',
    'KH': 'Cambodia',
    'TH': 'Thailand',
    'MM': 'Myanmar',
    'MY': 'Malaysia',
    'SG': 'Singapore',
    'ID': 'Indonesia',
    'PH': 'Philippines',
    'BN': 'Brunei',
    'TL': 'Timor-Leste',
    'AF': 'Afghanistan',
    'BD': 'Bangladesh',
    'BT': 'Bhutan',
    'NP': 'Nepal',
    'LK': 'Sri Lanka',
    'MV': 'Maldives',
    'PK': 'Pakistan',
    'AU': 'Australia',
    'NZ': 'New Zealand',
    'FJ': 'Fiji',
    'PG': 'Papua New Guinea',
    'SB': 'Solomon Islands',
    'VU': 'Vanuatu',
    'KI': 'Kiribati',
    'TV': 'Tuvalu',
    'NR': 'Nauru',
    'PW': 'Palau',
    'FM': 'Micronesia',
    'MH': 'Marshall Islands',
    'WS': 'Samoa',
    'TO': 'Tonga',
    'CK': 'Cook Islands',
    'NU': 'Niue',
    'TK': 'Tokelau',
    'AS': 'American Samoa',
    'GU': 'Guam',
    'MP': 'Northern Mariana Islands',
    'PF': 'French Polynesia',
    'NC': 'New Caledonia',
    'WF': 'Wallis and Futuna',
    'CL': 'Chile',
    'AR': 'Argentina',
    'BR': 'Brazil',
    'UY': 'Uruguay',
    'PY': 'Paraguay',
    'BO': 'Bolivia',
    'PE': 'Peru',
    'EC': 'Ecuador',
    'CO': 'Colombia',
    'VE': 'Venezuela',
    'GY': 'Guyana',
    'SR': 'Suriname',
    'GF': 'French Guiana',
    'MX': 'Mexico',
    'GT': 'Guatemala',
    'BZ': 'Belize',
    'SV': 'El Salvador',
    'HN': 'Honduras',
    'NI': 'Nicaragua',
    'CR': 'Costa Rica',
    'PA': 'Panama',
    'CU': 'Cuba',
    'JM': 'Jamaica',
    'HT': 'Haiti',
    'DO': 'Dominican Republic',
    'BS': 'Bahamas',
    'TT': 'Trinidad and Tobago',
    'BB': 'Barbados',
    'GD': 'Grenada',
    'VC': 'St Vincent and the Grenadines',
    'LC': 'Saint Lucia',
    'KN': 'Saint Kitts and Nevis',
    'AG': 'Antigua and Barbuda',
    'DM': 'Dominica',
    'AW': 'Aruba',
    'CW': 'Curacao',
    'SX': 'Sint Maarten',
    'BQ': 'Bonaire',
    'AI': 'Anguilla',
    'MS': 'Montserrat',
    'KY': 'Cayman Islands',
    'TC': 'Turks and Caicos',
    'VG': 'British Virgin Islands',
    'VI': 'US Virgin Islands',
    'PR': 'Puerto Rico',
    'GL': 'Greenland',
    'CA': 'Canada',
    'US': 'United States',
    'MX': 'Mexico'
  };

  // Normalize country name (handle codes, JSON, and standardize)
  const normalizeCountryName = (country) => {
    const parsed = parseCountryName(country);
    if (!parsed) return null;

    const upper = parsed.toUpperCase();
    // Check if it's a country code
    if (countryCodeMap[upper]) {
      return countryCodeMap[upper];
    }
    // Otherwise return as-is (title case for display)
    return parsed.charAt(0).toUpperCase() + parsed.slice(1).toLowerCase();
  };

  // Group and deduplicate country data with viewers
  const getCountryDataWithViewers = (countryBreakdown, viewers) => {
    if (!countryBreakdown || !viewers) return {};

    const countryGroups = {};

    // First, normalize all country keys and merge counts
    Object.entries(countryBreakdown).forEach(([rawCountry, data]) => {
      const normalized = normalizeCountryName(rawCountry);
      if (!normalized) return;

      if (!countryGroups[normalized]) {
        countryGroups[normalized] = {
          count: 0,
          percentage: 0,
          viewers: []
        };
      }
      countryGroups[normalized].count += data.count || 0;
      countryGroups[normalized].percentage += data.percentage || 0;
    });

    // Then assign viewers to their normalized countries
    viewers.forEach(viewer => {
      const normalized = normalizeCountryName(viewer.country);
      if (normalized && countryGroups[normalized]) {
        // Avoid duplicate viewers
        const exists = countryGroups[normalized].viewers.find(v => v.userId === viewer.userId);
        if (!exists) {
          countryGroups[normalized].viewers.push(viewer);
        }
      }
    });

    return countryGroups;
  };

  // Toggle accordion
  const toggleCountryAccordion = (country) => {
    setExpandedCountries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(country)) {
        newSet.delete(country);
      } else {
        newSet.add(country);
      }
      return newSet;
    });
  };

  // Fetch user info
  const fetchUserInfo = async () => {
    try {
      const allUserApis = require("../../apis/users");
      console.log("Fetching user info for userId:", userId);
      
      // Use the specific endpoint to get user details by ID
      const response = await allUserApis.getfordocumentverificationUserById(userId);
      console.log("User response:", response);
      
      if (response && response.data && response.data.length > 0) {
        const user = response.data[0]; // User data is in data array
        console.log("Found user:", user);
        setUserInfo(user);
      } else {
        setError("User not found");
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
      setError("Failed to fetch user information");
    }
  };

  // Fetch all analytics data
  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const start = formatDate(startDate);
      const end = formatDate(endDate);
      
      const [
        videoViewsData,
        feedViewsData,
        coinData,
        sentCoinsData,
        commentData,
        likeData,
        followData,
        followByUserData,
        interactionData
      ] = await Promise.all([
        analyticsApis.getTotalViews(start, end, userId),
        analyticsApis.getFeedTotalViews(start, end, userId),
        analyticsApis.getCoinAnalytics(start, end, userId),
        analyticsApis.getSentCoinsAnalytics(start, end, userId),
        analyticsApis.getCommentAnalytics(start, end, userId),
        analyticsApis.getLikeAnalytics(start, end, userId),
        analyticsApis.getFollowAnalytics(start, end, userId),
        analyticsApis.getFollowAnalyticsByUser(start, end, userId),
        analyticsApis.getUserInteractions(start, end, userId)
      ]);
      
      console.log("Video views data:", videoViewsData);
      console.log("Feed views data:", feedViewsData);

      console.log("Received coin data:", coinData);
      console.log("Received sent coins data:", sentCoinsData);

      console.log("Received video views data:", videoViewsData);
      console.log("Received feed views data:", feedViewsData);

      console.log("Received interaction data:", interactionData);

      setVideoViews(videoViewsData);
      setFeedViews(feedViewsData);
      setCoinAnalytics(coinData);
      setSentCoins(sentCoinsData);
      setCommentAnalytics(commentData);
      setLikeAnalytics(likeData);
      setFollowAnalytics(followData);
      setFollowAnalyticsByUser(followByUserData);
      setUserInteractions(interactionData);
      
    } catch (error) {
      console.error("Error fetching analytics data:", error);
      setError("Failed to fetch analytics data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserInfo();
  }, [userId]);

  // Initial data fetch on component mount
  useEffect(() => {
    if (startDate && endDate && userId) {
      fetchAnalyticsData();
    }
  }, [userId]);

  const handleDateRangeChange = () => {
    fetchAnalyticsData();
  };

  const renderMetricCard = (title, value, subtitle = "", color = "blue") => {
    return (
      <div className={`metric-card ${color}`}>
        <h3>{title}</h3>
        <div className="metric-value">{value || 0}</div>
        {subtitle && <div className="metric-subtitle">{subtitle}</div>}
      </div>
    );
  };

  const renderAnalyticsTable = (data, title, columns) => {
    if (!data || !data.payload || data.payload.length === 0) {
      return (
        <div className="analytics-section">
          <h3>{title}</h3>
          <div className="no-data">No data available for the selected period</div>
        </div>
      );
    }

    return (
      <div className="analytics-section">
        <h3>{title}</h3>
        <div className="table-container">
          <table className="analytics-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.payload.slice(0, 10).map((item, index) => (
                <tr key={index}>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? col.render(item[col.key]) : item[col.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!userInfo) {
    return (
      <IncludeSideBar>
        <div className="user-analytics-detail">
          <div className="loading">Loading user information...</div>
        </div>
      </IncludeSideBar>
    );
  }

  return (
    <IncludeSideBar>
      <div className="user-analytics-detail">
        <div className="header-section">
          <button className="back-btn" onClick={() => navigate("/user-analytics")}>
            ← Back to Users
          </button>
          <div className="user-info-header">
            {userInfo.profile_pic && (
              <img
                src={userInfo.profile_pic}
                alt={userInfo.first_name}
                className="user-avatar-large"
              />
            )}
            <div className="user-details">
              <h2>{userInfo.first_name} {userInfo.last_name}</h2>
              <p>@{userInfo.username} • {userInfo.email}</p>
              <span className={`account-type ${userInfo.account_type?.toLowerCase()}`}>
                {userInfo.account_type || "Basic"}
              </span>
            </div>
          </div>
        </div>

        <div className="date-range-section">
          <h3>Select Date Range</h3>
          <div className="date-picker-container">
            <div className="date-picker">
              <label>Start Date:</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={endDate}
              />
            </div>
            <div className="date-picker">
              <label>End Date:</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
              />
            </div>
            <button className="apply-btn" onClick={handleDateRangeChange}>
              Apply
            </button>
          </div>
        </div>

        {loading && <div className="loading">Loading analytics data...</div>}
        
        {error && <div className="error">{error}</div>}

        {!loading && !error && (
          <>
            <div className="metrics-grid">
              {renderMetricCard("Total Video Views", videoViews?.total_views_received || 0, "Videos", "blue")}
              {renderMetricCard("Total Feed Views", feedViews?.total_views_received || 0, "Feed", "green")}
              {renderMetricCard("Time Spent", formatTime(userInteractions?.total_time_spended), "On Platform", "indigo")}
              {renderMetricCard("Coins Received", coinAnalytics?.totalReceivedCoins || 0, "Received", "yellow")}
              {renderMetricCard("Coins Sent", sentCoins?.totalSentCoins || 0, "Sent", "purple")}
              {renderMetricCard("Comments", commentAnalytics?.totalReceivedComments || 0, "Posted", "orange")}
              {renderMetricCard("Likes", likeAnalytics?.total_like_received || 0, "Given", "red")}
              {renderMetricCard("Followers Gained", followAnalytics?.total_follow_initiated || 0, "Followers", "teal")}
              {renderMetricCard("Following", followAnalyticsByUser?.total_follow_initiated || 0, "Following", "pink")}
            </div>

            {/* Country Breakdown Section */}
            <div className="country-breakdown-section">
              <h3>Country Breakdown</h3>

              {videoViews?.countryBreakdown ? (
                Object.keys(videoViews.countryBreakdown).length > 0 ? (
                  <div className="country-breakdown-card">
                    <h4>Video Views by Country</h4>
                    <div className="country-list">
                      {Object.entries(getCountryDataWithViewers(videoViews.countryBreakdown, videoViews.viewers || []))
                        .sort(([,a], [,b]) => b.count - a.count)
                        .map(([country, data]) => (
                          <div key={country} className="country-accordion">
                            <div
                              className={`country-item ${expandedCountries.has(country) ? 'expanded' : ''}`}
                              onClick={() => toggleCountryAccordion(country)}
                            >
                              <span className="country-name">
                                <span className="accordion-arrow">{expandedCountries.has(country) ? '▼' : '▶'}</span>
                                {country}
                              </span>
                              <span className="country-count">{data.count} views</span>
                              <span className="country-percentage">({data.percentage.toFixed(2)}%)</span>
                            </div>
                            {expandedCountries.has(country) && data.viewers.length > 0 && (
                              <div className="viewers-list">
                                {data.viewers.map(viewer => (
                                  <div key={viewer.userId} className="viewer-item">
                                    <img
                                      src={viewer.profilePicture || `${MEDIA_SERVER_URL}/avatar/default.jpg`}
                                      alt={viewer.name}
                                      className="viewer-avatar"
                                      onError={(e) => { e.target.src = `${MEDIA_SERVER_URL}/avatar/default.jpg`; }}
                                    />
                                    <span className="viewer-name">{viewer.name}</span>
                                    <span className="viewer-id">ID: {viewer.userId}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {expandedCountries.has(country) && data.viewers.length === 0 && (
                              <div className="viewers-list empty">
                                <span className="no-viewers">No viewer details available</span>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div className="country-breakdown-card">
                    <h4>Video Views by Country</h4>
                    <div className="no-data">No country data available for video views</div>
                  </div>
                )
              ) : (
                <div className="country-breakdown-card">
                  <h4>Video Views by Country</h4>
                  <div className="no-data">Country data not available</div>
                </div>
              )}

              {feedViews?.countryBreakdown ? (
                Object.keys(feedViews.countryBreakdown).length > 0 ? (
                  <div className="country-breakdown-card">
                    <h4>Feed Views by Country</h4>
                    <div className="country-list">
                      {Object.entries(getCountryDataWithViewers(feedViews.countryBreakdown, feedViews.viewers || []))
                        .sort(([,a], [,b]) => b.count - a.count)
                        .map(([country, data]) => (
                          <div key={country} className="country-accordion">
                            <div
                              className={`country-item ${expandedCountries.has(`feed-${country}`) ? 'expanded' : ''}`}
                              onClick={() => toggleCountryAccordion(`feed-${country}`)}
                            >
                              <span className="country-name">
                                <span className="accordion-arrow">{expandedCountries.has(`feed-${country}`) ? '▼' : '▶'}</span>
                                {country}
                              </span>
                              <span className="country-count">{data.count} views</span>
                              <span className="country-percentage">({data.percentage.toFixed(2)}%)</span>
                            </div>
                            {expandedCountries.has(`feed-${country}`) && data.viewers.length > 0 && (
                              <div className="viewers-list">
                                {data.viewers.map(viewer => (
                                  <div key={viewer.userId} className="viewer-item">
                                    <img
                                      src={viewer.profilePicture || `${MEDIA_SERVER_URL}/avatar/default.jpg`}
                                      alt={viewer.name}
                                      className="viewer-avatar"
                                      onError={(e) => { e.target.src = `${MEDIA_SERVER_URL}/avatar/default.jpg`; }}
                                    />
                                    <span className="viewer-name">{viewer.name}</span>
                                    <span className="viewer-id">ID: {viewer.userId}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {expandedCountries.has(`feed-${country}`) && data.viewers.length === 0 && (
                              <div className="viewers-list empty">
                                <span className="no-viewers">No viewer details available</span>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div className="country-breakdown-card">
                    <h4>Feed Views by Country</h4>
                    <div className="no-data">No country data available for feed views</div>
                  </div>
                )
              ) : (
                <div className="country-breakdown-card">
                  <h4>Feed Views by Country</h4>
                  <div className="no-data">Country data not available</div>
                </div>
              )}
            </div>

            {/* Charts Section */}
            <div className="charts-section">
              <AnalyticsChart 
                data={videoViews} 
                title="Video Views Over Time" 
                dataKey="views" 
                color="#3498db" 
              />
              
              <AnalyticsChart 
                data={feedViews} 
                title="Feed Views Over Time" 
                dataKey="views" 
                color="#27ae60" 
              />
              
              <AnalyticsChart 
                data={coinAnalytics} 
                title="Coins Received Over Time" 
                dataKey="totalCoins" 
                color="#f39c12" 
              />
              
              <AnalyticsChart 
                data={sentCoins} 
                title="Coins Sent Over Time" 
                dataKey="totalCoins" 
                color="#9b59b6" 
              />
              
              <AnalyticsChart 
                data={commentAnalytics} 
                title="Comments Over Time" 
                dataKey="total_comments" 
                color="#e67e22" 
              />
              
              <AnalyticsChart 
                data={likeAnalytics} 
                title="Likes Over Time" 
                dataKey="total_like" 
                color="#e74c3c" 
              />
              
              <AnalyticsChart 
                data={followAnalytics} 
                title="Followers Gained Over Time" 
                dataKey="total_follows" 
                color="#16a085" 
              />
              
              <AnalyticsChart 
                data={followAnalyticsByUser} 
                title="Following Over Time" 
                dataKey="total_follows" 
                color="#e91e63" 
              />
              
              <AnalyticsChart 
                data={userInteractions} 
                title="Time Spent Over Time" 
                dataKey="total_interacted_time" 
                color="#3f51b5" 
              />
            </div>

            {/* {renderAnalyticsTable(videoViews, "Video Views Analytics", [
              { key: "video_id", label: "Video ID" },
              { key: "views", label: "Views" },
              { key: "date", label: "Date" }
            ])} */}

            {/* {renderAnalyticsTable(coinAnalytics, "Coins Received Analytics", [
              { key: "sender_id", label: "From User" },
              { key: "coins", label: "Coins" },
              { key: "date", label: "Date" }
            ])} */}

            {/* {renderAnalyticsTable(sentCoins, "Coins Sent Analytics", [
              { key: "receiver_id", label: "To User" },
              { key: "coins", label: "Coins" },
              { key: "date", label: "Date" }
            ])} */}

            {/* {renderAnalyticsTable(commentAnalytics, "Comments Analytics", [
              { key: "content", label: "Comment", render: (text) => text?.substring(0, 50) + "..." },
              { key: "video_id", label: "Video ID" },
              { key: "date", label: "Date" }
            ])} */}

            {/* {renderAnalyticsTable(likeAnalytics, "Likes Analytics", [
              { key: "video_id", label: "Video ID" },
              { key: "like_type", label: "Type" },
              { key: "date", label: "Date" }
            ])} */}
          </>
        )}
      </div>
    </IncludeSideBar>
  );
};

export default UserAnalyticsDetail;
